import { reactive, ref } from "vue";
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGE_CODES } from "./languages";

// ────────────────────────────────────────────────────────────────────────────
// German-source dictionary translation layer ($gt)
//
// The KARS UI is authored in German. Instead of hand-maintaining a separate
// key catalogue for ~235 views, the German source string IS the lookup key.
// Per-language dictionaries (auto-generated via the i18ntranslate tool) map
// each German string to its translation. Missing entries and the German
// locale itself fall back to the original text, so the UI can never break on
// a missing translation.
//
// This sits alongside vue-i18n (which keeps handling the hand-written,
// interpolated/pluralised keys for login, footer and the witness form).
// $gt deliberately avoids vue-i18n's key parsing (dots, pipes, "{}") so that
// arbitrary German sentences work as keys without escaping.
// ────────────────────────────────────────────────────────────────────────────

type AutoDict = Record<string, string>;

// Lazy glob of every auto-generated dictionary. Each language is fetched as
// its own chunk only the first time it is activated, so the default German
// build ships zero extra translation payload.
const autoLoaders = import.meta.glob<{ default: AutoDict }>("./auto/*.json");

// Reactive registry of dictionaries that have finished loading.
const dicts = reactive<Record<string, AutoDict>>({});
const loading = new Set<string>();

/**
 * Active UI language. Kept in sync with vue-i18n by the i18n module so that
 * both translation systems switch together. Reading it inside `gt()` makes
 * every component using `$gt` re-render when the language changes.
 */
export const activeLocale = ref<string>(DEFAULT_LANGUAGE);

/**
 * Ensure the auto-dictionary for `code` is loaded. Safe to call repeatedly;
 * it no-ops for German, unknown codes, and already-loaded / in-flight ones.
 */
export function ensureAutoLocaleLoaded(code: string): void {
  if (code === DEFAULT_LANGUAGE) return;
  if (!SUPPORTED_LANGUAGE_CODES.includes(code)) return;
  if (dicts[code] || loading.has(code)) return;

  const loader = autoLoaders[`./auto/${code}.json`];
  if (!loader) return;

  loading.add(code);
  loader()
    .then((mod) => {
      dicts[code] = mod.default ?? (mod as unknown as AutoDict);
    })
    .catch(() => {
      // Missing/broken dictionary → keep showing the German source text.
    })
    .finally(() => {
      loading.delete(code);
    });
}

/**
 * Identity marker for German source strings that are stored in non-reactive
 * places (data(), prop defaults, module constants) and translated later at
 * render time via `$gt(value)`. It exists purely so the extraction / check
 * tooling (scripts/i18n-check.mjs) can pick the string up as a dictionary key.
 */
export function gtKey(source: string): string {
  return source;
}

/**
 * Translate a German source string into the active UI language.
 *
 * Unknown strings and the German locale return the original text unchanged.
 */
export function gt(source: string): string {
  const code = activeLocale.value;
  if (code === DEFAULT_LANGUAGE) return source;

  const dict = dicts[code];
  if (!dict) {
    ensureAutoLocaleLoaded(code);
    return source;
  }
  return dict[source] ?? source;
}
